import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getProducts } from "@/api";
import { FadeIn } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const CATEGORIES = [
  { key: "all", label: "All Pieces" },
  { key: "short-kurta", label: "Short Kurtas" },
  { key: "long-kurta", label: "Long Kurtas" },
  { key: "cord-set", label: "Cord Sets" },
];

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";

  useEffect(() => {
    setLoading(true);
    getProducts()
      .then(setProducts)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    let list = category === "all" ? [...products] : products.filter((p) => p.category === category);
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    if (sort === "featured") list.sort((a, b) => Number(b.featured) - Number(a.featured));
    return list;
  }, [products, category, sort]);

  return (
    <div data-testid="shop-page" className="pt-20">
      <section className="py-9 sm:py-20 bg-ivory-deep/60 border-b border-gold/20">
        <div className="max-w-7xl mx-auto px-5 sm:px-12 lg:px-20">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.45em] text-gold-dark">The Debut Collection</p>
            <h1 className="mt-2 sm:mt-4 font-display text-3xl sm:text-5xl lg:text-6xl text-maroon-deep">The Collection</h1>
            <p className="mt-3 sm:mt-5 max-w-xl text-sm sm:text-base text-clay leading-[1.55] sm:leading-relaxed">
              Short kurtas, long kurtas and cord sets — each woven and finished by hand in India, photographed on the women they're made for.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-12 lg:px-20 py-6 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide w-full sm:w-auto -mx-1 px-1" role="tablist" aria-label="Filter by category">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                data-testid={`category-tab-${c.key}`}
                onClick={() => setParams(c.key === "all" ? {} : { category: c.key })}
                className={`px-5 py-2.5 text-[11px] tracking-[0.25em] uppercase border transition-all duration-300 ${
                  category === c.key
                    ? "bg-maroon text-ivory border-maroon"
                    : "border-maroon/20 text-maroon-deep hover:border-gold hover:text-gold-dark"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="w-full sm:w-auto bg-ivory-card border border-maroon/20 px-4 py-2.5 text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-maroon-deep focus:outline-none focus:border-gold"
          >
            <option value="featured">Signature First</option>
            <option value="price-asc">Price · Low to High</option>
            <option value="price-desc">Price · High to Low</option>
          </select>
        </div>

        {loading ? (
          <div data-testid="shop-loading" className="py-32 text-center">
            <p className="font-display italic text-2xl text-maroon-deep/60">Unfolding the collection…</p>
          </div>
        ) : visible.length === 0 ? (
          <div data-testid="shop-empty" className="py-32 text-center">
            <p className="font-display italic text-2xl text-maroon-deep/60">No pieces here yet — the loom is still at work.</p>
          </div>
        ) : (
          <div data-testid="shop-grid" className="mt-6 sm:mt-10 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10 sm:gap-x-8 sm:gap-y-12 lg:gap-x-12 lg:gap-y-16">
            {visible.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Shop;
